import React from 'react';
import { useBreakpointHelpers, VStack } from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useKustomerAvailability } from '../../../../../integrations/kustomer';
import type { AccountTabScreenId } from '../../../../../navigation/AppNavigation.props';
import { accountMenuStaticLinkConfig } from '../../../../../screens/AccountScreen/AccountScreen.navigation';
import { useSweetPassOptions } from '../../hooks';
import { AccountContactUsOption } from '../AccountContactUsOption';
import { AccountMenuLink } from '../AccountMenuLink';

export const AccountPrimaryMenu = (props: AccountPrimaryMenuProps) => {
  const { screenId, ...stackProps } = props;

  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();

  const isKustomerAvailable = useKustomerAvailability();
  const isReferFriendEnabled = useFeatureFlag('permanent-refer-friend-enabled');
  const { isSweetpassJoinItemVisible, isSweetpassMembershipItemVisible } =
    useSweetPassOptions();

  const shouldRenderContactUsOption = isMobile || !isKustomerAvailable;

  return (
    <VStack {...stackProps}>
      <AccountMenuLink
        testID="account.sidebar-tab.profile"
        label={t('account.sidebar.account-info')}
        iconName="IconProfileStroke"
        to={accountMenuStaticLinkConfig.profileLinkConfig}
        active={screenId === 'profile'}
      />

      {isSweetpassJoinItemVisible ? (
        <AccountMenuLink
          testID="account.sidebar-tab.sweetpass-join"
          label={t('account.sidebar.sweetpass-join')}
          iconName="IconLoyalty"
          tag={{
            title: t('account.sidebar.sweetpass-join.tag'),
            palette: 'primary',
          }}
          to={accountMenuStaticLinkConfig.sweetpassJoinLinkConfig}
          active={false}
        />
      ) : null}

      {isSweetpassMembershipItemVisible ? (
        <AccountMenuLink
          testID="account.sidebar-tab.sweetpass-membership"
          label={t('account.sidebar.sweetpass-membership')}
          iconName="IconLoyalty"
          to={accountMenuStaticLinkConfig.sweetpassMembershipLinkConfig}
          active={screenId === 'sweetpass-membership'}
        />
      ) : null}

      <AccountMenuLink
        testID="account.sidebar-tab.payment-promo"
        label={t('account.sidebar.payment-promo')}
        iconName="IconCreditCard"
        to={accountMenuStaticLinkConfig.paymentCreditLinkConfig}
        active={screenId === 'payment-credit'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.addresses"
        label={t('account.sidebar.addresses')}
        iconName="IconPickupStrokeSm"
        to={accountMenuStaticLinkConfig.addressesLinkConfig}
        active={screenId === 'addresses'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.orders"
        label={t('account.sidebar.orders')}
        iconName="IconBowl"
        to={accountMenuStaticLinkConfig.ordersLinkConfig}
        active={screenId === 'orders'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.favorites"
        label={t('account.sidebar.favorites')}
        iconName="IconHeartStroke"
        to={accountMenuStaticLinkConfig.favoritesLinkConfig}
        active={screenId === 'favorites'}
      />

      {isReferFriendEnabled ? (
        <AccountMenuLink
          testID="account.sidebar-tab.refer-friend"
          label={t('account.sidebar.refer-friend')}
          iconName="IconStarStroke"
          to={accountMenuStaticLinkConfig.referFriendLinkConfig}
          active={screenId === 'refer-friend'}
        />
      ) : null}

      <AccountMenuLink
        testID="account.sidebar-tab.dietary-restrictions"
        label={t('account.sidebar.dietary-restrictions')}
        iconName="IconFilter"
        to={accountMenuStaticLinkConfig.dietaryRestrictionsLinkConfig}
        active={screenId === 'dietary-restrictions'}
      />

      {shouldRenderContactUsOption ? <AccountContactUsOption /> : null}
    </VStack>
  );
};

type AccountPrimaryMenuProps = {
  readonly screenId?: AccountTabScreenId | null;
} & Omit<React.ComponentProps<typeof VStack>, 'children'>;
